import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import API_URL from '../api/baseURL';
import PayWithWaveButton from "./PayWithWaveButton";
import { generateDocumentPDF } from '../utils/generateDocumentPDF';
import { trackPixelCustom } from '../utils/metaPixel';

export default function OwnerQuotationInvoiceView({
  quotation,
  onActionDone,
  type = "quotation"
}) {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [responseNote, setResponseNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [localStatus, setLocalStatus] = useState(() => {
    return quotation && quotation.status ? quotation.status : "";
  });
  // Partial acceptance: indexes of the lines the client keeps. Starts with
  // everything selected, so doing nothing behaves exactly as before.
  const [keptLines, setKeptLines] = useState(() =>
    new Set((quotation?.items || []).map((_, i) => i))
  );

  if (!quotation) return <div className="p-8 text-[#8F877B]">Chargement…</div>;

  const workshop = quotation.workshopInfo || quotation.workshop || {};
  const client = quotation.clientInfo || quotation.client || {};
  const vehicle = quotation.vehicleInfo || quotation.vehicle || {};
  const items = quotation.items || [];
  // Line picking is only offered while the devis is still open for a decision.
  const selecting = localStatus === "pending" || localStatus === "countered";
  const lineTotal = (it) =>
    Number(it.clientPrice ?? it.unitPrice ?? it.price ?? 0) * Number(it.quantity ?? 0);
  const selectedTotal = items.reduce(
    (sum, it, i) => sum + (keptLines.has(i) ? lineTotal(it) : 0), 0
  );
  const someDeselected = selecting && keptLines.size < items.length;
  const toggleLine = (i) =>
    setKeptLines((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });

  // [FIX] Use stored subtotal/tax if available
  // Use client-facing totals when available (10% platform markup)
  const discountPct = Number(quotation.discountPercent) || 0;
  const clientSubtotalFull = (quotation.items || []).reduce(
    (s, it) => s + (Number(it.clientPrice || it.price) || 0) * (Number(it.quantity) || 1), 0
  );
  const discountAmount = discountPct > 0 ? Math.round(clientSubtotalFull * discountPct / 100) : 0;
  // When a remise applies, show the full client subtotal as the "Subtotal" line.
  const subtotal = discountPct > 0
    ? Math.round(clientSubtotalFull)
    : (quotation.clientSubtotal || quotation.subtotal || (quotation.total || 0));
  const tax = quotation.tax !== undefined ? quotation.tax : 0;
  const total = quotation.clientTotal || quotation.total || (subtotal + tax) || 0;

  // This now handles multiple naming conventions and types for deposit/downpayment
  const downpayment =
    quotation.depositAmount !== undefined && quotation.depositAmount !== null
      ? Number(quotation.depositAmount)
      : quotation.downpayment !== undefined && quotation.downpayment !== null
        ? Number(quotation.downpayment)
        : Math.max(Math.round(Number(total) * 0.1), 1);

  // --- NEW: Helper to get the counter-offer (from quotation or local prop)
  const counterOffer =
    typeof quotation.counterOffer !== "undefined"
      ? quotation.counterOffer
      : null;

  const handleDecision = async (status) => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/workshop-quotations/${quotation._id}/decision`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          clientNote: responseNote,
          // Only meaningful on accept; the API ignores it otherwise.
          acceptedItems: [...keptLines].sort((a, b) => a - b),
        })
      });
      setLocalStatus(status);
      if (onActionDone) onActionDone();

      if (status === "accepted") {
        trackPixelCustom('QuotationAccepted', { value: Number(quotation.clientTotal || quotation.total) || 0, currency: 'XOF' });
        // Fetch (or create) the invoice, then redirect user to pay there
        const res = await fetch(`${API_URL}/invoices/from-quotation/${quotation._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const invoice = await res.json();
          navigate(`/owner/invoice/${invoice._id}`);
          return;
        } else {
          // Fallback: show info to user
          alert("Quotation accepted. Please visit your invoice page to proceed with payment.");
        }
      }
    } catch (err) {
      alert("Failed to update quotation status");
    }
    setLoading(false);
  };

  const handleRevision = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/workshop-quotations/${quotation._id}/reply`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ workshopResponseNote: responseNote })
      });
      setLocalStatus("revised");
      if (onActionDone) onActionDone();
    } catch (err) {
      alert("Failed to send revision request");
    }
    setLoading(false);
  };

  return (
    <div className="bg-gradient-to-b from-[#17140F] to-[#131110] border border-[#241F1B] text-[#F4F1EA] max-w-3xl mx-auto rounded-2xl p-6 my-6">
      {/* RECOMMENDED PAYMENT BANNER */}
      <div className="mb-5 bg-[#DFA23C]/10 text-[#DFA23C] px-4 py-3 rounded-xl border border-[#DFA23C]/25">
        <b>Note :</b> Après acceptation d’un devis, un <b>acompte est requis</b> pour confirmer la réservation.<br />
        <b>Nous recommandons fortement de payer via l’application</b> pour garantir la traçabilité et éviter tout litige avec l’atelier.<br />
        L’atelier ne commencera les travaux qu’après réception de votre acompte.
      </div>

      {/* Workshop Info */}
      <div className="flex flex-col md:flex-row md:justify-between border-b border-[#241F1B] pb-3 mb-4">
        <div>
          <div className="text-xl font-bold text-[#F4F1EA]">
            {workshop.businessName || workshop.details?.businessName || workshop.name || "-"}
          </div>
          <div>{workshop.address || "-"}</div>
          <div>{workshop.email || "-"}</div>
          <div>{workshop.contactNumber || "-"}</div>
          {workshop.taxId && (
            <div><b>N° TVA :</b> {workshop.taxId}</div>
          )}
        </div>
        <div className="mt-2 md:mt-0 flex items-center gap-2">
          <span className="bg-[#241F1B] text-[#C9C3B6] rounded px-3 py-1">
            {(type || quotation.type) === "invoice" ? "FACTURE" : "DEVIS"}
          </span>
          <button
            onClick={() => generateDocumentPDF(quotation, type || 'quotation', {
              businessName: workshop.businessName || workshop.details?.businessName || workshop.name,
              address: workshop.address,
              phone: workshop.contactNumber,
              email: workshop.email,
              taxId: workshop.taxId,
              logo: workshop.logo,
            })}
            className="px-3 py-1.5 rounded-[9px] bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0] hover:from-white text-[#141210] text-sm font-semibold transition-colors flex items-center gap-1"
            title="Télécharger PDF"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            PDF
          </button>
        </div>
      </div>

      {/* Client */}
      <div className="mb-2 p-3 rounded bg-[#100F0D]">
        <div className="font-semibold text-[#74A98D]">
          {client.name || client.username || client.email || "-"}
        </div>
        <div>{client.details?.location || "-"}</div>
        <div>
          <b>Véhicule :</b>{" "}
          {vehicle.brand || "-"} {vehicle.model || ""} - {vehicle.plateNumber || ""}
        </div>
      </div>

      {/* Items */}
      <table className="min-w-full bg-[#100F0D] text-xs rounded mb-4">
        <thead>
          <tr>
            {selecting && (
              <th className="p-2 w-8">
                <input
                  type="checkbox"
                  aria-label="Tout sélectionner"
                  checked={keptLines.size === items.length && items.length > 0}
                  onChange={(e) =>
                    setKeptLines(e.target.checked ? new Set(items.map((_, i) => i)) : new Set())
                  }
                />
              </th>
            )}
            <th className="p-2">Catégorie</th>
            <th className="p-2">Désignation</th>
            <th className="p-2">Référence</th>
            <th className="p-2">Marque</th>
            <th className="p-2">Qté</th>
            <th className="p-2">Prix unitaire</th>
            <th className="p-2">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const kept = !selecting || keptLines.has(idx);
            // A line the client already declined stays visible but struck out.
            const declined = !selecting && item.accepted === false;
            return (
            <tr
              key={idx}
              className={(!kept || declined) ? "opacity-40 line-through" : ""}
            >
              {selecting && (
                <td className="p-2">
                  <input
                    type="checkbox"
                    aria-label={`Garder ${item.designation || item.description || "cette ligne"}`}
                    checked={keptLines.has(idx)}
                    onChange={() => toggleLine(idx)}
                  />
                </td>
              )}
              <td className="p-2">{item.category || "-"}</td>
              <td className="p-2">{item.designation || item.description || "-"}</td>
              <td className="p-2">{item.reference || "-"}</td>
              <td className="p-2">{item.brand || "-"}</td>
              <td className="p-2">{item.quantity ?? 0}</td>
              <td className="p-2">
                {Number(item.clientPrice ?? item.unitPrice ?? item.price ?? 0).toLocaleString("fr-CI")} FCFA
              </td>
              <td className="p-2">
                {Number(lineTotal(item)).toLocaleString("fr-CI")} FCFA
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>

      {/* Partial acceptance summary — only while a decision is open */}
      {selecting && (
        <div className="-mt-2 mb-4 flex flex-wrap items-center justify-between gap-2 text-[12.5px]">
          <span className="text-[#8F877B]">
            {keptLines.size} ligne{keptLines.size > 1 ? "s" : ""} sur {items.length} sélectionnée{keptLines.size > 1 ? "s" : ""}
            {someDeselected && " — le reste ne sera pas facturé"}
          </span>
          {someDeselected && (
            <span className="text-[#F4F1EA]">
              Total sélectionné :{" "}
              <b className="font-['Space_Grotesk',sans-serif] tabular-nums">
                {Number(selectedTotal).toLocaleString("fr-CI")} FCFA
              </b>
            </span>
          )}
        </div>
      )}

      {/* The client must be able to see that the ATELIER confirmed for them —
          this is the visible, disputable trail for a walk-in confirmation. */}
      {quotation.confirmedBy?.role === "workshop" && (
        <div
          className="-mt-2 mb-4 rounded-xl px-4 py-2.5 text-[12.5px]"
          style={{ background: "rgba(223,162,60,.13)", color: "#DFA23C" }}
        >
          Confirmé par l’atelier
          {quotation.confirmedBy?.at &&
            ` le ${new Date(quotation.confirmedBy.at).toLocaleDateString("fr-FR")}`}
          {" — "}si vous n’avez pas donné votre accord, contactez l’atelier ou 1PARC.
        </div>
      )}

      {/* Shown after the fact when the client kept only part of the devis */}
      {!selecting && quotation.partiallyAccepted && (
        <div
          className="-mt-2 mb-4 rounded-xl px-4 py-2.5 text-[12.5px]"
          style={{ background: "rgba(223,162,60,.13)", color: "#DFA23C" }}
        >
          Devis partiellement accepté — les lignes barrées ont été refusées et ne sont pas facturées.
        </div>
      )}

      {/* Diagnostic / Notes */}
      {quotation.note && (
        <div className="mb-3 p-3 bg-[#1B1815] border border-[#2B2724] rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[#C9C3B6] text-sm font-bold">Rapport Diagnostic</span>
          </div>
          <span className="text-[#C9C3B6]">{quotation.note}</span>
        </div>
      )}

      {/* --- Totals Section (NEW) --- */}
      <div className="flex flex-col items-end mt-2 space-y-1">
        <div>
          <b>Sous-total :</b> {Number(subtotal).toLocaleString("fr-CI")} FCFA
        </div>
        {discountPct > 0 && (
          <div className="text-[#74A98D]">
            <b>Remise {discountPct}% :</b> −{Number(discountAmount).toLocaleString("fr-CI")} FCFA
          </div>
        )}
        {tax > 0 && (
          <div>
            <b>TVA (18%) :</b> {Number(tax).toLocaleString("fr-CI")} FCFA
          </div>
        )}
        <div>
          <b>Total :</b> {Number(total).toLocaleString("fr-CI")} FCFA
        </div>
        {downpayment > 0 && downpayment < Number(total) && (
          <div>
            <b>Acompte requis :</b> {Number(downpayment).toLocaleString("fr-CI")} FCFA
          </div>
        )}
        <div>
          <b>Payé :</b> {Number(quotation.paidAmount || 0).toLocaleString("fr-CI")} FCFA
        </div>
        <div>
          <b>Reste à payer :</b> {(Number(total) - Number(quotation.paidAmount || 0)).toLocaleString("fr-CI")} FCFA
        </div>
        {!quotation.paid && (() => {
          const paid = Number(quotation.paidAmount || 0);
          const depositStillOwed = paid < Number(downpayment) && downpayment > 0 && downpayment < Number(total);
          const nowDue = depositStillOwed
            ? Math.round(Number(downpayment) - paid)
            : Math.round(Number(total) - paid);
          return (
            <div className="text-[#DFA23C] font-bold mt-2">
              Amount to Pay Now: {nowDue.toLocaleString("fr-CI")} FCFA
            </div>
          );
        })()}
      </div>
      {/* --- END TOTALS BLOCK --- */}

      {/* --- NEGOTIATION FLOW UI --- */}

      {/* Owner sees the workshop's counter-offer */}
      {localStatus === "countered" && (
        <div className="my-4">
          <div className="mb-2 text-[#DFA23C] font-bold">
            L'atelier a proposé une contre-offre de:{" "}
            <span className="text-[#74A98D]">
              {counterOffer
                ? Number(counterOffer).toLocaleString("fr-CI")
                : "-"}{" "}
              FCFA
            </span>
          </div>
          <div className="flex gap-3 justify-end">
            <button
              className="px-4 py-2 rounded bg-[#2F5D48] hover:bg-[#25493A]"
              disabled={loading}
              onClick={() => handleDecision("accepted")}
            >
              Accepter la contre-offre
            </button>
            <button
              className="px-4 py-2 rounded bg-[#5A2420] hover:bg-[#6B2B26]"
              disabled={loading}
              onClick={() => handleDecision("rejected")}
            >
              Rejeter
            </button>
            {/* You can add a "Counter Again" button here if you want multi-step negotiation */}
          </div>
        </div>
      )}

      {/* Pending: original negotiation actions */}
      {localStatus === "pending" && (
        <div className="my-4">
          <textarea
            className="w-full rounded bg-[#100F0D] text-white p-2"
            value={responseNote}
            onChange={e => setResponseNote(e.target.value)}
            placeholder="Votre note ou demande de négociation (facultatif)…"
          />
          <div className="flex gap-3 justify-end mt-2">
            <button
              className="px-4 py-2 rounded bg-[#5A2420] hover:bg-[#6B2B26]"
              disabled={loading}
              onClick={() => handleDecision("rejected")}
            >
              Refuser
            </button>
            <button
              className="px-4 py-2 rounded bg-[#6B5220] hover:bg-[#7D6026]"
              disabled={loading}
              onClick={handleRevision}
            >
              Demander une révision
            </button>
            <button
              className="px-4 py-2 rounded bg-[#2F5D48] hover:bg-[#25493A]"
              disabled={loading}
              onClick={() => handleDecision("accepted")}
            >
              Accepter
            </button>
          </div>
        </div>
      )}

      {/* Status */}
      {localStatus && (
        <div className="mt-3 text-right text-sm text-[#C9C3B6]">
          Status: <span className="font-semibold">{localStatus}</span>
        </div>
      )}

      {/* ---------------- PAYMENT UI for INVOICE ONLY ---------------- */}
      {type === "invoice" && !quotation.paid && (() => {
        const alreadyPaid = Number(quotation.paidAmount || 0);
        const balanceDue = Math.round(Number(total) - alreadyPaid);
        const depositNotYetPaid = alreadyPaid < Number(downpayment) && downpayment > 0 && downpayment < Number(total);
        const amountToPay = depositNotYetPaid
          ? Math.round(Number(downpayment) - alreadyPaid)
          : balanceDue;

        return (
          <div className="my-6 bg-[#1B1815] border border-[#2B2724] rounded-xl p-5 text-center">
            <div className="text-lg font-bold text-white mb-3">Payment Required</div>
            <div className="mb-3 space-y-1">
              <div className="text-[#C9C3B6]">
                <span className="text-[#8F877B]">Total facture :</span> <span className="text-white font-medium">{Number(total).toLocaleString("fr-CI")} FCFA</span>
              </div>
              {alreadyPaid > 0 && (
                <div className="text-[#74A98D] font-semibold">
                  Déjà payé : {alreadyPaid.toLocaleString("fr-CI")} FCFA
                </div>
              )}
              {depositNotYetPaid ? (
                <div className="text-[#DFA23C] font-semibold">
                  Downpayment Required: {Number(downpayment).toLocaleString("fr-CI")} FCFA
                  {alreadyPaid > 0 && ` (restant : ${amountToPay.toLocaleString("fr-CI")} FCFA)`}
                </div>
              ) : (
                <div className="text-[#DFA23C] font-semibold">
                  Balance Due: {balanceDue.toLocaleString("fr-CI")} FCFA
                </div>
              )}
            </div>
            <div className="mb-4 text-[#8F877B] text-sm">
              {depositNotYetPaid
                ? "Your service will not start until the downpayment is received."
                : "Please pay the remaining balance to complete this invoice."
              }
            </div>
            <PayWithWaveButton
              vehicleId={quotation.vehicle?._id}
              amount={amountToPay}
              type="invoice"
              workshopId={quotation.workshop?._id}
              invoiceId={quotation._id}
              onSuccess={() => window.location.reload()}
            />
          </div>
        );
      })()}
      {type === "invoice" && quotation.paid && (
        <div className="my-6 rounded-xl p-4 text-center font-semibold" style={{background:'rgba(116,169,141,.13)',color:'#74A98D'}}>
          Payment received! ✅ The garage will start your service soon.
        </div>
      )}
      {/* -------------------------------------------------------------- */}
    </div>
  );
}
